import type { TFunction } from 'i18next'

import { useFunkitTranslation } from '../providers/FunkitI18nProvider'
import {
  type PaymentMethodOption,
  SelectMethodScreen,
} from './SelectMethodScreen'

export interface WithdrawMethodScreenProps {
  /** Cash payout methods (Card / Bank Transfer / …). */
  methods: PaymentMethodOption[]
  onSelectMethod: (id: string) => void
  /** Sheet title — the flow resolves it from the host config. */
  title: string
  loading?: boolean
  /** The payout-method fetch failed — shows the retryable error state. */
  hasError?: boolean
  onRetry?: () => void
  /**
   * Withdrawals unavailable (Statsig gate off, or Swapped returned no methods
   * for the user's region) — shows a terminal, non-retryable message.
   */
  isEmpty?: boolean
  /**
   * The balance can't meet Swapped's minimum sell — shows a distinct terminal
   * message instead of methods that would all reject.
   */
  isBelowMinimum?: boolean
  /** Why the cash rails are missing (web's `NoCashMethodsNote`). */
  note?: string
}

// Below-minimum wins over empty — a too-low balance is the more specific reason.
function getTerminalError(
  t: TFunction,
  isBelowMinimum: boolean,
  isEmpty: boolean,
) {
  if (isBelowMinimum) {
    return {
      title: t('withdraw.belowMinimumTitle'),
      message: t('withdraw.belowMinimumMessage'),
    }
  }
  if (isEmpty) {
    return {
      title: t('withdraw.unavailableTitle'),
      message: t('withdraw.unavailableMessage'),
    }
  }
  return null
}

/**
 * The withdrawal payout-method picker: the entry screen of `beginWithdrawal`,
 * listing the Swapped `CRYPTO_TO_FIAT` methods. A thin composition over
 * {@link SelectMethodScreen} (no receive-token dropdown → the bare-list layout),
 * so the list/loading/error visuals stay in lockstep with the deposit flow.
 */
export function WithdrawMethodScreen(props: WithdrawMethodScreenProps) {
  const { t } = useFunkitTranslation()
  const {
    methods,
    onSelectMethod,
    title,
    loading = false,
    hasError = false,
    onRetry,
    isEmpty = false,
    isBelowMinimum = false,
    note,
  } = props
  // Fetch errors and loading win over terminal states — a failed fetch says
  // nothing about availability or balance, and retrying may recover it.
  const terminalError = getTerminalError(t, isBelowMinimum, isEmpty)
  if (terminalError && !hasError && !loading) {
    return (
      <SelectMethodScreen
        title={title}
        methods={[]}
        onSelectMethod={onSelectMethod}
        error
        errorTitle={terminalError.title}
        errorMessage={terminalError.message}
      />
    )
  }
  return (
    <SelectMethodScreen
      title={title}
      methods={methods}
      onSelectMethod={onSelectMethod}
      loading={loading}
      error={hasError}
      onRetry={onRetry}
      errorTitle={t('selectMethod.errorTitle')}
      errorMessage={t('selectMethod.errorMessage')}
      note={note}
    />
  )
}
